import React, { PropsWithChildren } from 'react';

import {
  ChakraVStack,
  FAIconName,
  FireflyButton,
  Text,
} from '@equip.health/ui';

import InquiryDrawerLayout from '~/components/common/inquiry/drawer/InquiryDrawerLayout';

interface EntryProps extends PropsWithChildren {
  header: string;
  id: string;
}

/**
 * Render entry 1-N where the title indicates which entity index this is.
 * @param children The child element.
 * @param header Header text.
 * @constructor
 */
const Entry = ({ id, children, header }: EntryProps) => {
  return (
    <ChakraVStack alignItems="start" gap="12px">
      <Text id={`${id}-header`} variant="bodyMedium" width="100px">
        {header}
      </Text>
      {children}
    </ChakraVStack>
  );
};

export interface ExpandableSectionProps extends PropsWithChildren {
  id: string;
  addButtonText: string;
  entityType: string;
  onAdd: () => void;
  headerText: string;
}

/**
 * Render an Expandable Section.
 * @param children The children rendered within.
 * @param addButtonText The text to show on the add button.
 * @param headerText The header text.
 * @param id The id
 * @param entityType Description of entities rendered within. This is used for display purposes.
 * @param onAdd Method to call in reaction to add button click.
 * @constructor
 */
const ExpandableSection = ({
  children,
  addButtonText,
  headerText,
  id,
  entityType,
  onAdd,
}: ExpandableSectionProps) => {
  const childrenArray = React.Children.toArray(children);

  return (
    <InquiryDrawerLayout header={headerText} id={id}>
      <ChakraVStack gap="36px" width="100%">
        {childrenArray.map((child, index) => {
          // Only the first entry does not get a header.
          return index == 0 ? (
            <React.Fragment key={index}>{child}</React.Fragment>
          ) : (
            <Entry
              header={`${entityType} ${index + 1}`}
              id={`${id}__entry${index}`}
              key={index}
            >
              {child}
            </Entry>
          );
        })}
      </ChakraVStack>
      <FireflyButton
        id={`${id}__add-button`}
        isFullWidth
        leftIcon={FAIconName.plusSolid}
        onClick={onAdd}
        variant="secondary"
      >
        {addButtonText}
      </FireflyButton>
    </InquiryDrawerLayout>
  );
};

export default ExpandableSection;
